/**
 * Layer 6 — ReportViewModel  (audited: Sheet30 layer)
 * ─────────────────────────────────────────────────────────────────────────────
 * Transforms the validated interpretation output into the exact `BlueprintResult`
 * shape consumed by the Dashboard UI (`src/pages/Dashboard.jsx`).
 *
 * This layer is intentionally UI-oriented:
 *   - Adds per-section `_meta.source` badges.
 *   - Adds top-level `engineVersion`, `sourceStatus`, `inputHash`,
 *     `variantId`, `variantLabel`, `profile`, and back-compat flat fields.
 *   - Attaches the appropriate disclaimer for the current source mix.
 *
 * The ReportViewModel MUST NOT perform any Life Blueprint calculation.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { SOURCE_STATUS, ENGINE_VERSION } from "./sourceStatus";
import { stableHash } from "./normalize";

const MOCK_DISCLAIMER =
  "本页面所有内容均为 PoC 演示用示例结果 (MOCK_POC)，非最终计算引擎输出。仅供个人规划参考，不构成投资、医疗、法律或任何结果保证。";

const V299_DISCLAIMER =
  "结果由已验证的 Life Blueprint V2.9.9 规则生成 (VALIDATED_V299)。内容仅供个人规划参考，不构成投资、医疗、法律或任何结果保证。";

function metaFor(source, note) {
  return { source, note: note || null };
}

/**
 * @param {import('./coreRuleEngine').CoreOutput} coreOutput
 * @param {import('./analysisEngine').AnalysisOutput} analysisOutput
 * @param {import('./interpretationEngine').InterpretationOutput} interpretation
 * @param {import('./schema').NormalizedInput} normalized
 * @param {import('./schema').RawCustomerInput} raw
 * @returns {import('./schema').BlueprintResult}
 */
export function buildReportViewModel(coreOutput, analysisOutput, interpretation, normalized, raw) {
  const isV299 =
    coreOutput.source === SOURCE_STATUS.VALIDATED_V299 &&
    analysisOutput.source === SOURCE_STATUS.VALIDATED_V299 &&
    interpretation.source === SOURCE_STATUS.VALIDATED_V299;

  const engineVersion = isV299 ? ENGINE_VERSION.V299 : ENGINE_VERSION.POC_MOCK;
  const sourceStatus = isV299 ? SOURCE_STATUS.VALIDATED_V299 : SOURCE_STATUS.MOCK_POC;
  const disclaimer = isV299 ? V299_DISCLAIMER : MOCK_DISCLAIMER;

  const variantId = analysisOutput.categorized?.variantId || "";
  const variantLabel = analysisOutput.categorized?.variantLabel || "";

  const summaryCardsSection = {
    _meta: metaFor(sourceStatus, isV299 ? null : "PoC illustrative content"),
    ...interpretation.summaryCards,
  };
  const overviewSection = {
    _meta: metaFor(sourceStatus, isV299 ? null : "PoC illustrative content"),
    metrics: interpretation.overviewMetrics,
  };
  const themesSection = {
    _meta: metaFor(sourceStatus, isV299 ? null : "PoC placeholder taxonomy — real 12-theme names await V2.9.9"),
    items: interpretation.themes,
    taxonomyVersion: isV299 ? "V2.9.9" : "POC-PLACEHOLDER",
  };
  const numbersSection = {
    _meta: metaFor(sourceStatus, isV299 ? null : "PoC placeholder — real 24-number rules await V2.9.9"),
    favorable: interpretation.numbers.favorable,
    unfavorable: interpretation.numbers.unfavorable,
    usage: interpretation.numbers.usage,
  };
  const wealthSection = {
    _meta: metaFor(sourceStatus, isV299 ? null : "PoC placeholder — real wealth blueprint rules await V2.9.9"),
    focus: interpretation.wealth.focus,
    priority: interpretation.wealth.priority,
    watch: interpretation.wealth.watch,
    next: interpretation.wealth.next,
  };

  return {
    engineVersion,
    sourceStatus,
    inputHash: stableHash(normalized.rawKey),
    variantId,
    variantLabel,
    profile: {
      name: raw?.name || normalized.name || "访客",
      birthDate: raw?.birthDate || normalized.birthDateISO,
      birthTime: raw?.birthTime || "",
      gender: raw?.gender || "",
      region: raw?.region || "",
    },
    // Structured sections
    summaryCardsSection,
    overviewSection,
    themesSection,
    numbersSection,
    wealthSection,
    // Back-compat flat fields (Dashboard currently reads these)
    summaryCards: interpretation.summaryCards,
    overviewMetrics: interpretation.overviewMetrics,
    themes: interpretation.themes,
    numbers: interpretation.numbers,
    wealth: interpretation.wealth,

    disclaimer,
  };
}
